import {
    Flag,
    LANGUAGE_INFO,
} from '@gagovictor/shared-frontend-core/dist/i18n';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import {
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Language, LANGUAGES, loadContent, useI18n } from '../../i18n';
import { RootState } from '../../store';
import { playCue } from '../feedback';
import { sound } from '../sound';
import { setMuted } from '../tarotSlice';

import { ReminderMenuItem, useReminderToggle } from './ReminderToggle';

/** Language and sound live under one gear, so the narrow toolbar stays clear. */
export const SettingsMenu: React.FC = () => {
    const { t, language, setLanguage } = useI18n();
    const dispatch = useDispatch();
    const muted = useSelector((s: RootState) => s.tarot.muted);
    const reminders = useReminderToggle();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const close = () => setAnchor(null);
    // The language whose readings are downloading, and the one that failed to; the menu stays open meanwhile.
    const [loading, setLoading] = useState<Language | null>(null);
    const [failed, setFailed] = useState<Language | null>(null);

    const chooseLanguage = (code: Language) => {
        if (code === language || loading) return;
        setFailed(null);
        setLoading(code);
        loadContent(code)
            .then(() => {
                setLoading(null);
                close();
                setLanguage(code);
            })
            .catch(() => {
                setLoading(null);
                setFailed(code);
            });
    };

    return (
        <>
            <Tooltip title={t('app.settings')}>
                <IconButton
                    color="inherit"
                    aria-label={t('app.settings')}
                    aria-haspopup="menu"
                    aria-expanded={anchor ? true : undefined}
                    onClick={(e) => setAnchor(e.currentTarget)}
                    data-testid="settings-menu"
                >
                    <SettingsRoundedIcon />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
                <ListSubheader>{t('app.language')}</ListSubheader>
                {LANGUAGES.map((code: Language) => (
                    <MenuItem
                        key={code}
                        selected={code === language}
                        disabled={Boolean(loading) && loading !== code}
                        onClick={() => chooseLanguage(code)}
                    >
                        <ListItemIcon>
                            {loading === code ? (
                                <CircularProgress
                                    size={18}
                                    aria-label={t('app.loading')}
                                />
                            ) : (
                                <Flag
                                    country={LANGUAGE_INFO[code].country}
                                    size={18}
                                />
                            )}
                        </ListItemIcon>
                        <ListItemText
                            secondary={failed === code && t('app.loadError')}
                        >
                            {LANGUAGE_INFO[code].nativeName}
                        </ListItemText>
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem
                    onClick={() => {
                        const next = !muted;
                        dispatch(setMuted(next));
                        sound.setMuted(next);
                        if (!next) playCue('ui');
                    }}
                >
                    <ListItemIcon>
                        {muted ? (
                            <VolumeOffRoundedIcon />
                        ) : (
                            <VolumeUpRoundedIcon />
                        )}
                    </ListItemIcon>
                    <ListItemText>{t('app.sound')}</ListItemText>
                </MenuItem>
                <ReminderMenuItem
                    on={reminders.on}
                    onToggle={() => {
                        close();
                        reminders.toggle();
                    }}
                />
            </Menu>
            {reminders.toast}
        </>
    );
};
